import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import estimateProvider from "@/providers/estimate.provider";
import type { PasteLineItemsResponse, PasteLineItemPayload } from "@/providers/estimate.provider";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";

export function usePasteLineItemsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: PasteLineItemPayload): Promise<PasteLineItemsResponse> => {
      const response = await estimateProvider.pasteLineItems(params);

      if (!response.success) {
        throw new Error(response.error ?? "Failed to paste line items");
      }

      return response;
    },
    onSuccess: (response, variables) => {
      const totalCopied = response.data?.totalCopied ?? 0;
      const totalFailed = response.data?.totalFailed ?? 0;

      if (totalCopied > 0) {
        toast.success(`${totalCopied} item${totalCopied > 1 ? "s" : ""} pasted`);
      }
      if (totalFailed > 0) {
        toast.error(`${totalFailed} item${totalFailed > 1 ? "s" : ""} failed to paste`);
      }

      queryClient.invalidateQueries({
        queryKey: ESTIMATE_KEYS.detail(variables.estimateId),
      });
    },
    onError: (error) => {
      toast.error("Failed to paste line items");
      console.error("Paste line items error:", error);
    },
  });
}
